import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Grid,
  Chip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CreateCustomPlan from './CreateCustomPlan';
import EditCustomPlan from './EditCustomPlan';
import apiClient from "../../request/http-common";
import { useMutation } from "react-query";

const UserCustomPlans = ({ customPlans, userId, onUpdate }) => {
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const { mutate: deletePlan } = useMutation(
    async (planId) => {
      return await apiClient.delete(`/api/custom-plans/${planId}`);
    },
    {
      onSuccess: (res) => {
        if (res.data.status === 'success') {
          localStorage.setItem('notification', JSON.stringify({
            message: res.data.message,
            type: 'success'
          }));
          const event = new Event('newMessage');
          window.dispatchEvent(event);
          if (onUpdate) onUpdate();
        }
      },
      onError: (err) => {
        const errorMessage = err.response?.data.message || 'An error occurred';
        localStorage.setItem('notification', JSON.stringify({
          message: errorMessage,
          type: 'error'
        }));
        const event = new Event('newMessage');
        window.dispatchEvent(event);
      }
    }
  );

  const handleEdit = (plan) => {
    setSelectedPlan(plan);
    setEditOpen(true);
  };

  const handleDelete = (planId) => {
    if (window.confirm('Are you sure you want to delete this custom plan?')) {
      deletePlan(planId);
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Custom Investment Plans</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Create Custom Plan
        </Button>
      </Box>

      {customPlans && customPlans.length > 0 ? (
        <Grid container spacing={2}>
          {customPlans.map((plan) => (
            <Grid item xs={12} md={6} key={plan.id}>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      {plan.name}
                    </Typography>
                    <Box>
                      <IconButton size="small" onClick={() => handleEdit(plan)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(plan.id)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Min Amount:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ${parseFloat(plan.min_amount).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Max Amount:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ${parseFloat(plan.amount_invested).toLocaleString()}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Total ROI:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        {plan.total_earning}%
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Amount/Hour:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        ${plan.amount_per_hour}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Duration:
                      </Typography>
                      <Typography variant="body1">
                        {plan.duration}
                      </Typography>
                    </Grid>
                    {plan.extra_note && (
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Extra Note:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontStyle: 'italic',
                            p: 1,
                            bgcolor: 'action.hover',
                            borderRadius: 1
                          }}
                        >
                          {plan.extra_note}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card>
          <CardContent>
            <Typography variant="body1" color="text.secondary" textAlign="center">
              No custom plans created for this user yet.
            </Typography>
          </CardContent>
        </Card>
      )}

      <CreateCustomPlan
        open={createOpen}
        handleClose={() => setCreateOpen(false)}
        userId={userId}
        onSuccess={onUpdate}
      />

      <EditCustomPlan
        open={editOpen}
        handleClose={() => setEditOpen(false)}
        customPlan={selectedPlan}
        onSuccess={onUpdate}
      />
    </Box>
  );
};

export default UserCustomPlans;

